import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import clsx from 'clsx'

import Cropper from '../../Common/Cropper/Cropper'

import { deleteImage, saveImageData, setImageError } from '../../../store/actions/image.actions'
import { setCanvasInited, updateCanvasValues } from '../../../store/actions/canvas.actions'
import { hideLoader, showLoader } from '../../../store/actions/loader.actions'
import { applyAdjustAndFilters } from '../../../helpers/adjust'
import {
  getLastPhysicsSetting,
  getLastFiltersSetting,
  getLastAdjustSetting,
  getLastSetting
} from '../../../helpers/util'
import { sendMetric } from '../../../helpers/metric'
import throttle from '../../../helpers/throttle'
import { withTranslation } from 'react-i18next'

class Canvas extends PureComponent {
  static propTypes = {
    imageData: PropTypes.object,
    imageSrc: PropTypes.string.isRequired,
    imageSrcUploaded: PropTypes.string.isRequired,
    imageFormat: PropTypes.string.isRequired,
    imageType: PropTypes.string.isRequired,
    settingsList: PropTypes.array.isRequired,
    currentSettingId: PropTypes.number.isRequired,
    lastSettingId: PropTypes.number.isRequired,
    cropperIsActive: PropTypes.bool.isRequired,
    canvas: PropTypes.object.isRequired,
    resize: PropTypes.object.isRequired,
    updateCanvasValues: PropTypes.func.isRequired,
    hideLoader: PropTypes.func.isRequired,
    showLoader: PropTypes.func.isRequired,
    saveImageData: PropTypes.func.isRequired,
    fileSize: PropTypes.number.isRequired,
    isCanvasInited: PropTypes.bool.isRequired,
    setImageError: PropTypes.func.isRequired,
    deleteImage: PropTypes.func.isRequired,
    dispatchSetCanvasInited: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)

    if (this.props.imageSrcUploaded) {
      this.shouldHandlePrevImage = true
    }

    this.throttledApplyFilters = throttle(() => this.applyFilters(), 1000 / 60)

    this.canvasRef = React.createRef()
    this.canvasWrapperRef = React.createRef()
  }

  componentDidMount() {
    const { imageSrc } = this.props
    if (imageSrc) this.initCanvas()
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize)
  }

  componentDidUpdate(prevProps) {
    const { imageSrc, settingsList, currentSettingId, imageData } = this.props

    const currentSetting = getLastSetting(settingsList, currentSettingId)
    const previousSetting = getLastSetting(prevProps.settingsList, prevProps.currentSettingId)
    const currentPhysicsSetting = getLastPhysicsSetting(settingsList, currentSettingId)
    const previousPhysicsSetting = getLastPhysicsSetting(prevProps.settingsList, prevProps.currentSettingId)

    if (imageSrc && imageSrc !== prevProps.imageSrc) {
      this.initCanvas()
    }

    if (currentPhysicsSetting !== previousPhysicsSetting || (imageData && !prevProps.imageData)) {
      this.applyResize()
    }

    if ((currentSetting !== previousSetting || (imageData && !prevProps.imageData)) && this.shadowCanvas) {
      this.throttledApplyFilters()
    }
  }

  handleResize = () => {
    const {
      imageData,
      updateCanvasValues
    } = this.props

    if (!imageData) {
      return
    }

    const {
      canvasWidth,
      canvasHeight
    } = this.calcCanvasRect()

    if (this.canvasRef.current.width !== canvasWidth || this.canvasRef.current.height !== canvasHeight) {
      this.applyResize()
      this.applyFilters()
    } else {
      const {
        canvasX,
        canvasY
      } = this.calcCanvasPosition()

      updateCanvasValues({
        x: canvasX,
        y: canvasY
      })
    }
  }

  prepareImageData = (image) => {
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    canvas.width = image.width
    canvas.height = image.height
    context.drawImage(image, 0, 0, image.width, image.height)
    return context.getImageData(0, 0, image.width, image.height)
  }

  initCanvas = () => {
    const {
      imageSrc,
      canvas,
      hideLoader,
      showLoader,
      saveImageData,
      imageFormat,
      imageType,
      fileSize,
      setImageError,
      deleteImage,
      t
    } = this.props

    const image = new Image()
    image.crossOrigin = 'Anonymous'
    image.src = imageSrc

    this.shadowCanvas = document.createElement('canvas')
    const context = this.canvasRef.current.getContext('2d')
    context.clearRect(0, 0, canvas.width, canvas.height)

    showLoader(t('loader.upload-image'))

    image.onload = () => {
      window.addEventListener('resize', this.handleResize)

      if (imageSrc.substring(0, 5) === 'blob:') {
        URL.revokeObjectURL(imageSrc)
      }

      sendMetric({
        event: 'FILE_ADDED',
        gtm: {
          eventCategory: 'Interactions',
          eventAction: 'click',
          eventLabel: 'FILE_ADDED'
        },
        amplitude: {
          INPUT_FILE_EXTENSION: imageFormat,
          INPUT_FILE_SIZE: fileSize,
          INPUT_FILE_WIDTH: image.width,
          INPUT_FILE_HEIGHT: image.height,
          INPUT_FILE_RESOLUTION: `${image.width}x${image.height}`,
          UPLOAD_TYPE: imageType,
        }
      })

      saveImageData(this.prepareImageData(image))
      hideLoader()

      if (this.shouldHandlePrevImage) {
        this.shouldHandlePrevImage = false
      }
    }

    image.onerror = () => {
      hideLoader()
      deleteImage()
      if (!this.shouldHandlePrevImage) {
        setImageError(true)
      }

      this.shouldHandlePrevImage = false
    }  
  }

  applyFilters = () => {
    const {
      settingsList,
      currentSettingId
    } = this.props
    const shadowContext = this.shadowCanvas.getContext('2d')
    const context = this.canvasRef.current.getContext('2d')
    const lastAdjustSetting = getLastAdjustSetting(settingsList, currentSettingId)
    const lastFiltersSetting = getLastFiltersSetting(settingsList, currentSettingId)

    const imageUpdateData = shadowContext.getImageData(
      0,
      0,
      this.canvasRef.current.width,
      this.canvasRef.current.height
    )
    let updatedImageData = applyAdjustAndFilters(imageUpdateData, lastAdjustSetting, lastFiltersSetting)

    context.putImageData(updatedImageData, 0, 0)
  }

  applyResize = () => {
    const {
      imageData,
      canvas,
      updateCanvasValues,
      settingsList,
      currentSettingId,
      dispatchSetCanvasInited
    } = this.props

    const {
      canvasWidth,
      canvasHeight,
      canvasScaleWidth,
      canvasScaleHeight
    } = this.calcCanvasRect()

    const shadowContext = this.shadowCanvas.getContext('2d')
    const canvasResize = document.createElement('canvas')
    const resizeContext = canvasResize.getContext('2d')

    const lastSetting = getLastPhysicsSetting(settingsList, currentSettingId)

    this.canvasRef.current.width = canvasWidth
    this.canvasRef.current.height = canvasHeight
    this.shadowCanvas.width = canvasWidth
    this.shadowCanvas.height = canvasHeight

    const newImageData = lastSetting ? lastSetting.imageData : imageData
    canvasResize.width = newImageData.width
    canvasResize.height = newImageData.height

    resizeContext.putImageData(newImageData, 0, 0)
    shadowContext.drawImage(canvasResize, 0, 0, canvasWidth, canvasHeight)

    const {
      canvasX,
      canvasY
    } = this.calcCanvasPosition()

    const newCanvas = {
      ...canvas,
      x: canvasX,
      y: canvasY,
      width: canvasWidth,
      height: canvasHeight,
      scaleWidth: canvasScaleWidth,
      scaleHeight: canvasScaleHeight
    }

    updateCanvasValues(newCanvas)
    dispatchSetCanvasInited(true)
  }

  calcCanvasPosition = () => {
    return {
      canvasX: this.canvasRef.current.offsetLeft - this.canvasWrapperRef.current.offsetLeft,
      canvasY: this.canvasRef.current.offsetTop - this.canvasWrapperRef.current.offsetTop
    }
  }

  calcCanvasRect = () => {
    const { imageData, settingsList, currentSettingId } = this.props
    if (!this.canvasWrapperRef.current) return

    // temporarily hide canvas in order to be able to change the size down
    this.canvasRef.current.hidden = true

    const wrapperWidth = this.canvasWrapperRef.current.offsetWidth
    const wrapperHeight = this.canvasWrapperRef.current.offsetHeight

    // restore canvas visibility
    this.canvasRef.current.hidden = false

    const lastSetting = getLastPhysicsSetting(settingsList, currentSettingId)
    const imageWidth = lastSetting ? lastSetting.imageData.width : imageData.width
    const imageHeight = lastSetting ? lastSetting.imageData.height : imageData.height
    let canvasWidth
    let canvasHeight
    let canvasScaleWidth = 1
    let canvasScaleHeight = 1

    canvasWidth = imageWidth
    canvasHeight = imageHeight

    if (imageWidth > wrapperWidth || imageHeight > wrapperHeight) {
      const scale = Math.min(wrapperWidth / imageWidth, wrapperHeight / imageHeight)

      canvasWidth = Math.ceil(imageWidth * scale)
      canvasHeight = Math.ceil(imageHeight * scale)
      canvasScaleWidth = canvasWidth / imageWidth
      canvasScaleHeight = canvasHeight / imageHeight
    }

    return {
      canvasWidth,
      canvasHeight,
      canvasScaleWidth,
      canvasScaleHeight
    }
  }

  render() {
    const {
      imageSrc,
      cropperIsActive,
      isCanvasInited
    } = this.props

    return (
      <div className={clsx('canvas-wrapper', {'canvas-wrapper_hidden': !imageSrc})} ref={this.canvasWrapperRef}>
        <canvas
          className="canvas"
          ref={this.canvasRef}
          onContextMenu={(e)=> e.preventDefault()}
        />
        {isCanvasInited && cropperIsActive && <Cropper />}
      </div>
    )
  }
}

const mapStateToProps = ({
  imageStore: {
    imageSrc,
    imageSrcUploaded,
    imageData,
    imageFormat,
    imageType
  },
  cropperStore: {
    cropperIsActive
  },
  canvasStore: {
    canvas,
    isCanvasInited
  },
  resizeStore: {
    resize
  },
  settingsStore: {
    settingsList,
    currentSettingId,
    lastSettingId
  },
}) => ({
  imageSrc,
  imageSrcUploaded,
  imageData,
  imageFormat,
  imageType,
  cropperIsActive,
  canvas,
  resize,
  settingsList,
  currentSettingId,
  lastSettingId,
  isCanvasInited
})

const mapDispatchToProps = (dispatch) => ({
  setImageError: (value) => dispatch(setImageError(value)),
  deleteImage: () => dispatch(deleteImage()),
  updateCanvasValues: (values) => dispatch(updateCanvasValues(values)),
  hideLoader: () => dispatch(hideLoader()),
  showLoader: (message) => dispatch(showLoader(message)),
  saveImageData: (data) => dispatch(saveImageData(data)),
  dispatchSetCanvasInited: (isInited) => dispatch(setCanvasInited(isInited)),
})

export default withTranslation()(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(Canvas))
